import i18n from 'i18n-js';

import { Alert, localTime, formatDateToLocalDateTime, injectConditionalArrayItems } from '@helpers';
import { OVERPRODUCTION_MULTIPLIER, PAST_DATE_WARNING_DAYS, TIME_OVERRUN_MULTIPLIER } from '@v2/components/FinishJobModal/constants';
import type { GetSubmitValidationParams, SubmitValidationResult, SubmitWarning } from '@v2/components/FinishJobModal/types';

// Splits the Finish Job submit checks into hard errors (block submit) and warnings (confirm-and-continue).
export const getSubmitValidation = ({
  producedQuantity,
  quantityTarget,
  actualSetupTimeInSeconds,
  actualCycleTimeTotalInSeconds,
  scheduledSetupTimeInSeconds,
  scheduledCycleTimeTotalInSeconds,
  actualEndDate,
  jobStartDate,
}: GetSubmitValidationParams): SubmitValidationResult => {
  // Compare in factory time (localTime) so the checks line up with how dates are shown to the user.
  const jobStart = jobStartDate ? localTime(jobStartDate) : null;
  const actualEnd = localTime(actualEndDate);

  // HARD: BE rejects when Actual End is before the job's start. Mirror it client-side so the user gets
  // immediate feedback instead of a silent revert from SignalR. Include the time — the block can trigger
  // on a same-day, earlier-time difference where date-only would read as two equal dates.
  const isBeforeJobStart = !!jobStart && jobStart.isValid() && actualEnd.isBefore(jobStart);
  const errors: SubmitWarning[] = injectConditionalArrayItems(isBeforeJobStart, {
    title: i18n.t('Invalid Date'),
    text: `${i18n.t('Actual End')}: ${formatDateToLocalDateTime(actualEndDate)} — ${i18n.t('Start')}: ${jobStartDate ? formatDateToLocalDateTime(jobStartDate) : ''}`,
  });

  const scheduledTotal = scheduledSetupTimeInSeconds + scheduledCycleTimeTotalInSeconds;
  const actualTotal = actualSetupTimeInSeconds + actualCycleTimeTotalInSeconds;

  const isUnreasonableQuantity = quantityTarget > 0 && producedQuantity > quantityTarget * OVERPRODUCTION_MULTIPLIER;
  const isOverProduced = quantityTarget > 0 && producedQuantity > quantityTarget;
  const isTimeOverrun = scheduledTotal > 0 && actualTotal > scheduledTotal * TIME_OVERRUN_MULTIPLIER;
  const isFutureDate = actualEnd.isAfter(localTime());
  const isOldDate = actualEnd.isBefore(localTime().subtract(PAST_DATE_WARNING_DAYS, 'days'));

  const warnings: SubmitWarning[] = [
    ...injectConditionalArrayItems(producedQuantity === 0, {
      title: i18n.t('Zero Quantity'),
      text: i18n.t('Complete job with zero pieces produced?'),
    }),
    ...injectConditionalArrayItems(isUnreasonableQuantity, {
      title: i18n.t('Unreasonable Quantity'),
      text: i18n.t('Produced quantity seems unreasonably high. Continue?'),
    }),
    ...injectConditionalArrayItems(!isUnreasonableQuantity && isOverProduced, {
      title: i18n.t('Over-Production'),
      text: i18n.t("You've produced {{count}} extra pieces. Continue?", { count: producedQuantity - quantityTarget }),
    }),
    ...injectConditionalArrayItems(isTimeOverrun, {
      title: i18n.t('High Actual Time'),
      text: i18n.t('Actual time is significantly higher than scheduled. Is this correct?'),
    }),
    ...injectConditionalArrayItems(isFutureDate, {
      title: i18n.t('Future Date'),
      text: i18n.t('Completion date is in the future. Continue?'),
    }),
    ...injectConditionalArrayItems(isOldDate, {
      title: i18n.t('Old Date'),
      text: i18n.t('Date is more than 30 days in the past. Continue?'),
    }),
  ];

  return { errors, warnings };
};

// Hard error: blocking alert with a single OK; submit does not proceed.
export const showErrorAlert = (error: SubmitWarning): void => {
  Alert({
    title: error.title,
    text: error.text,
    actions: [{ text: i18n.t('OK'), style: 'primary' }],
  });
};

// Warnings: show each as a Cancel/Continue dialog in sequence; onConfirm runs only after the last Continue.
export const showWarningChain = (warnings: SubmitWarning[], index: number, onConfirm: () => void): void => {
  if (index >= warnings.length) {
    onConfirm();
    return;
  }

  Alert({
    title: warnings[index].title,
    text: warnings[index].text,
    actions: [
      { text: i18n.t('Cancel'), style: 'primary' },
      { text: i18n.t('Continue'), onPress: () => showWarningChain(warnings, index + 1, onConfirm) },
    ],
  });
};
